import React from 'react';
import { Box, VStack, Input, FormControl, FormLabel } from '@chakra-ui/react';
import { KPIConfiguration } from 'src/components/molecules/KPIConfiguration';
import { ModelHeader } from 'src/components/molecules/ModelHeader';
import { ModelTypeRadio } from 'src/components/molecules/ModelTypeRadio';
import { MarketingDataTable } from '../../molecules/MarketingDataTable';
import { CampaignMapping } from 'src/api/campaignMappings';
import type { ModelType } from 'src/api/models';

interface KPIOption {
  value: string;
  label: string;
}

interface MediaPlatform {
  value: string;
  label: string;
}

// Define the structure for a single model form
export interface ModelFormData {
  id: string;
  modelName: string;
  modelType?: ModelType;
  kpiSource: string;
  kpiMetric: string;
  kpiUnit: string;
  selectedMedia: string[];
  campaignSelections: {
    [campaignId: string]: {
      [media: string]: boolean;
    };
  };
  searchValue: string;
}

interface ModelFormProps {
  model: ModelFormData;
  modelIndex: number;
  onRemove?: () => void;
  canRemove?: boolean;
  modelsLength: number;
  // Handler functions
  onModelNameChange: (modelId: string, value: string) => void;
  onModelTypeChange: (modelId: string, value: ModelType) => void;
  onSourceChange: (modelId: string, value: string) => Promise<void>;
  onSourceReset: (modelId: string) => void;
  onMetricChange: (modelId: string, value: string) => void;
  onMetricReset: (modelId: string) => void;
  onUnitChange: (modelId: string, value: string) => void;
  onUnitReset: (modelId: string) => void;
  onMediaChange: (modelId: string, index: number, value: string) => void;
  onAddColumn: (modelId: string) => void;
  onRemoveColumn: (modelId: string) => void;
  onCampaignToggle: (
    modelId: string,
    campaignId: string,
    media: string,
  ) => void;
  onSelectAllForMedia: (modelId: string, media: string) => void;
  onSearchValueChange: (modelId: string, value: string) => void;
  // Data
  kpiSources: KPIOption[];
  kpiMetrics: KPIOption[];
  isLoading: boolean;
  mediaPlatforms: MediaPlatform[];
  campaignMappings: CampaignMapping[];
  searchedCampaigns: CampaignMapping[];
  sortOrder: 'asc' | 'desc' | null;
  handleSortToggle: () => void;
  isAllCampaignsSelectedForMedia: (
    model: ModelFormData,
    media: string,
  ) => boolean;
  hasAvailableMedia: (model: ModelFormData) => boolean;
}

export const ModelForm: React.FC<ModelFormProps> = ({
  model,
  modelIndex,
  onRemove,
  canRemove = true,
  modelsLength,
  onModelNameChange,
  onModelTypeChange,
  onSourceChange,
  onSourceReset,
  onMetricChange,
  onMetricReset,
  onUnitChange,
  onUnitReset,
  onMediaChange,
  onAddColumn,
  onRemoveColumn,
  onCampaignToggle,
  onSelectAllForMedia,
  onSearchValueChange,
  kpiSources,
  kpiMetrics,
  isLoading,
  mediaPlatforms,
  campaignMappings,
  searchedCampaigns,
  sortOrder,
  handleSortToggle,
  isAllCampaignsSelectedForMedia,
  hasAvailableMedia,
}) => {
  return (
    <VStack spacing={6} align="stretch" minH="400px">
      {/* Model Header with Remove Button */}
      <ModelHeader
        modelIndex={modelIndex}
        modelsLength={modelsLength}
        onRemove={onRemove}
        canRemove={canRemove}
      />

      {/* Model Name Section */}
      <Box>
        <FormControl>
          <FormLabel
            fontSize="16px"
            fontWeight={500}
            color="content.title"
            mb={3}>
            Model name
          </FormLabel>
          <Input
            placeholder="Name your model"
            value={model.modelName}
            onChange={(e) => onModelNameChange(model.id, e.target.value)}
            bg="surface.panel"
            border="1px solid"
            borderColor="surface.border"
            borderRadius="md"
            py={3}
            px={4}
            fontSize="16px"
            _placeholder={{ color: 'content.muted' }}
            _focus={{
              borderColor: 'icon.green',
              boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
            }}
          />
        </FormControl>
      </Box>

      {/* Model Type Section */}
      <ModelTypeRadio
        value={model.modelType}
        onChange={(next) => onModelTypeChange(model.id, next)}
        hiddenOptions={['lt']}
      />

      {/* KPI Configuration Section */}
      <KPIConfiguration
        model={model}
        onSourceChange={onSourceChange}
        onSourceReset={onSourceReset}
        onMetricChange={onMetricChange}
        onMetricReset={onMetricReset}
        onUnitChange={onUnitChange}
        onUnitReset={onUnitReset}
        kpiSources={kpiSources}
        kpiMetrics={kpiMetrics}
        isLoading={isLoading}
      />

      {/* Marketing Data Section */}
      <MarketingDataTable
        model={model}
        onMediaChange={onMediaChange}
        onAddColumn={onAddColumn}
        onRemoveColumn={onRemoveColumn}
        onCampaignToggle={onCampaignToggle}
        onSelectAllForMedia={onSelectAllForMedia}
        mediaPlatforms={mediaPlatforms}
        campaignMappings={campaignMappings}
        searchedCampaigns={searchedCampaigns}
        searchValue={model.searchValue}
        setSearchValue={(value) => onSearchValueChange(model.id, value)}
        sortOrder={sortOrder}
        handleSortToggle={handleSortToggle}
        isAllCampaignsSelectedForMedia={isAllCampaignsSelectedForMedia}
        hasAvailableMedia={hasAvailableMedia}
      />
    </VStack>
  );
};
